// src/components/layout/Sidebar.jsx
import React from 'react';
import { Shield, Users, Key, FileText, Clock, CheckCircle, BarChart3, Home, ChevronRight, Package, UserCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ sidebarOpen, setSidebarOpen, currentPage, expandedMenus, handleMenuClick }) => {
  const { user } = useAuth();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      page: 'dashboard'
    },
    {
      id: 'admin',
      label: 'Administration',
      icon: Shield,
      children: [
        { id: 'users', label: 'Users', icon: Users, page: 'users' },
        { id: 'roles', label: 'Roles', icon: Shield, page: 'roles' },
        { id: 'appmenu', label: 'App Menus', icon: Key, page: 'appmenu' },
        { id: 'permissions', label: 'Permissions', icon: Key, page: 'permissions' }
      ]
    },
    ...(user?.role === 'ADMIN' ? [
      {
        id: 'customers',
        label: 'Customers',
        icon: UserCheck,
        children: [
          { id: 'customers', label: 'All Customers', icon: FileText, page: 'customers' }
        ]
      },     
      {
        id: 'expenses',
        label: 'Expenses',
        icon: Package,
        children: [
          //{ id: 'expenses', label: 'All Expenses', icon: FileText, page: 'expenses' },
          { id: 'trackexpenses', label: 'Track Expenses', icon: FileText, page: 'trackexpenses' }
          
        ]
      }
    ] : []),
     {
        id: 'products',
        label: 'Products',
        icon: Package,
        children: [
          { id: 'products', label: 'All Products', icon: FileText, page: 'products' }
        ]
      },
    {
      id: 'invoices',
      label: 'Invoices',
      icon: FileText,
      children: [
        ...(user?.role === 'USER' ? [
          { id: 'invoices-all', label: 'New Invoices', icon: FileText, page: 'invoices' },
          { id: 'approved-invoices', label: 'Paid Invoices', icon: CheckCircle, page: 'approved-invoices' }
        ] : []),
        ...(user?.role === 'ADMIN' ? [
          { id: 'invoices-all', label: 'New Invoices', icon: FileText, page: 'invoices' },
          { id: 'pending-invoices', label: 'Pending Invoices', icon: Clock, page: 'pending-invoices' },
          { id: 'statement', label: 'Statement', icon: Clock, page: 'statement' }
         
        ] : [])
      ]
    },
    ...(user?.role === 'ADMIN' ? [
      {
        id: 'reports',
        label: 'Reports',
        icon: BarChart3,
         children: [
          { id: 'report', label: 'Sales Analytics', icon: FileText, page: 'report' },
          { id: 'profit-loss', label: 'Profit & Loss', icon: FileText, page: 'profit-loss' },
        ]
      }
    ] : [])
  ];

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => {
    if (user?.role === 'ADMIN') return true;
    if (item.id === 'admin') return false;
    return true;
  });

  return (
    <>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full bg-white shadow-lg z-50 transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:z-30
        ${sidebarOpen ? 'w-64' : 'w-64 lg:w-16'}
      `}>
        <div className="flex items-center justify-between p-4 border-b min-h-[5rem]">
          <div className={`flex items-center ${sidebarOpen || window.innerWidth >= 1024 ? 'space-x-3' : 'justify-center'}`}>
            <div className="bg-indigo-600 rounded-lg p-2">
              <Shield className="w-6 h-6 text-white" />
            </div>
            {(sidebarOpen || window.innerWidth >= 1024) && (
              <div className="lg:hidden xl:block">
                <h1 className="text-xl font-bold text-gray-900">InvoicePro</h1>
                <p className="text-xs text-gray-500">Management System</p>
              </div>
            )}
          </div>
        </div>
        
        <nav className="mt-6 pb-20 overflow-y-auto h-full">
          {filteredMenuItems.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => {
                  handleMenuClick(item.page, !!item.children, item.id);
                  // Close sidebar on mobile when clicking menu item
                  if (window.innerWidth < 1024 && !item.children) {
                    setSidebarOpen(false);
                  }
                }}
                className={`w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                  currentPage === item.page ? 'bg-indigo-50 border-r-2 border-indigo-600 text-indigo-600' : 'text-gray-700'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {(sidebarOpen || window.innerWidth >= 1024) && (
                  <>
                    <span className="ml-3 flex-1 lg:hidden xl:block">{item.label}</span>
                    {item.children && (
                      <ChevronRight className={`w-4 h-4 transition-transform lg:hidden xl:block ${
                        expandedMenus[item.id] ? 'rotate-90' : ''
                      }`} />
                    )}
                  </>
                )}
              </button>
              
              {item.children && (sidebarOpen || window.innerWidth >= 1024) && expandedMenus[item.id] && (
                <div className="bg-gray-50">
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => {
                        handleMenuClick(child.page);
                        // Close sidebar on mobile when clicking submenu item
                        if (window.innerWidth < 1024) {
                          setSidebarOpen(false);
                        }
                      }}
                      className={`w-full flex items-center px-8 py-2 text-left hover:bg-gray-100 transition-colors ${
                        currentPage === child.page ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600'
                      }`}
                    >
                      <child.icon className="w-4 h-4 mr-3" />
                      <span className="text-sm lg:hidden xl:block">{child.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

export default Sidebar;